import { Box, ButtonBase, makeStyles } from "@material-ui/core";
import * as React from "react";

import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";

export type ManagementDisplayMode = "simple" | "detail";

const useStyles = makeStyles({
  root: {
    display: "flex",
    gap: layout.space[4],
    padding: layout.space[4],
    width: "100%",
    background: colors["surface/card"],
    border: `1px solid ${colors["border/default"]}`,
    borderRadius: layout.radius.full,
  },
  option: {
    flex: 1,
    minWidth: 0,
    minHeight: 36,
    padding: "8px 12px",
    borderRadius: layout.radius.full,
    color: colors["text/secondary"],
    ...textStyles["SP/Button/M"],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Button/L"], minHeight: 40 },
    "&:focus-visible": { outline: `2px solid ${colors["border/focus"]}`, outlineOffset: 2 },
  },
  selected: { color: colors["text/inverse"], background: colors["action/primary"], boxShadow: theme.shadows[1] },
});

export const ManagementDisplayControl: React.FC<{
  value: ManagementDisplayMode;
  onChange: (value: ManagementDisplayMode) => void;
}> = ({ value, onChange }) => {
  const classes = useStyles();
  const options = [
    { value: "simple" as const, label: "簡易表示" },
    { value: "detail" as const, label: "詳細表示" },
  ];
  const buttons = React.useRef<Array<HTMLButtonElement | null>>([]);
  const select = (index: number) => {
    buttons.current[index]?.focus();
    if (value !== options[index].value) onChange(options[index].value);
  };
  return (
    <Box className={classes.root} id="management-tour-view" role="tablist" aria-label="収支の表示方法">
      {options.map((option, index) => (
        <ButtonBase
          key={option.value}
          component="button"
          type="button"
          role="tab"
          id={`management-display-${option.value}`}
          aria-selected={value === option.value}
          aria-controls="management-cashflow-content"
          tabIndex={value === option.value ? 0 : -1}
          ref={(button: HTMLButtonElement | null) => {
            buttons.current[index] = button;
          }}
          className={`${classes.option} ${value === option.value ? classes.selected : ""}`}
          onClick={() => {
            if (value !== option.value) onChange(option.value);
          }}
          onKeyDown={(event) => {
            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
              event.preventDefault();
              select(event.key === "Home" ? 0 : event.key === "End" ? 1 : 1 - index);
            }
          }}
        >
          {option.label}
        </ButtonBase>
      ))}
    </Box>
  );
};
